import type { VariableDef } from '../runtime/Variables';

/**
 * Code generation for rendering a design from an application.
 *
 * The generated code is intentionally dependency-free and readable: the ANSI
 * frame as a string constant, a variable substitution pass, and a render
 * function. Nothing here couples the project to one runtime.
 */

export type CodeTarget = 'typescript' | 'javascript' | 'rust' | 'python' | 'go';

export interface CodeTargetInfo {
  id: CodeTarget;
  name: string;
  extension: string;
}

export const CODE_TARGETS: readonly CodeTargetInfo[] = [
  { id: 'typescript', name: 'TypeScript', extension: 'ts' },
  { id: 'javascript', name: 'JavaScript (Node)', extension: 'js' },
  { id: 'rust', name: 'Rust', extension: 'rs' },
  { id: 'python', name: 'Python', extension: 'py' },
  { id: 'go', name: 'Go', extension: 'go' },
];

export interface CodegenInput {
  name: string;
  width: number;
  height: number;
  ansi: string;
  variables: readonly VariableDef[];
}

function escapeForTs(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\$\{/g, '\\${')
    .replace(/\u001b/g, '\\u001b')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n');
}

function escapeForRust(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\u001b/g, '\\x1b')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n');
}

function escapeForPython(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\u001b/g, '\\x1b')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n');
}

function identifier(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]+/g, ' ').trim();
  const parts = cleaned.split(/\s+/);
  const camel = parts
    .map((p, i) => (i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()))
    .join('');
  return /^[A-Za-z_]/.test(camel) ? camel : `screen${camel}`;
}

export function generateCode(target: CodeTarget, input: CodegenInput): string {
  switch (target) {
    case 'typescript':
      return generateTypeScript(input, true);
    case 'javascript':
      return generateTypeScript(input, false);
    case 'rust':
      return generateRust(input);
    case 'python':
      return generatePython(input);
    case 'go':
      return generateGo(input);
  }
}

function generateTypeScript(input: CodegenInput, typed: boolean): string {
  const fn = identifier(input.name);
  const vars = input.variables;
  const typeLines = vars
    .map((v) => `  ${v.name}${v.defaultValue === undefined ? '' : '?'}: ${tsType(v)};`)
    .join('\n');

  return `// Generated by Pixansi from "${input.name}" (${input.width}x${input.height}).
// Plain string substitution — no dependencies, safe to edit.

${typed && vars.length ? `export interface ${capitalize(fn)}Data {\n${typeLines}\n}\n` : ''}
const FRAME = \`${escapeForTs(input.ansi)}\`;

const DEFAULTS${typed ? `: Record<string, string>` : ''} = {
${vars.map((v) => `  ${v.name}: ${JSON.stringify(String(v.defaultValue ?? v.fallback ?? ''))},`).join('\n')}
};

/** Renders the frame with the supplied values substituted. */
export function ${fn}(data${typed ? `: Record<string, string | number | boolean> = {}` : ' = {}'})${
    typed ? ': string' : ''
  } {
  const values = { ...DEFAULTS, ...data };
  return FRAME.replace(/\\{\\{\\s*([A-Za-z_][A-Za-z0-9_]*)\\s*(?:\\|[^}]*)?\\}\\}/g, (match, name) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });
}

// Example:
// process.stdout.write(${fn}({ ${vars
    .slice(0, 2)
    .map((v) => `${v.name}: ${JSON.stringify(v.previewValue ?? '')}`)
    .join(', ')} }));
`;
}

function tsType(v: VariableDef): string {
  switch (v.type) {
    case 'integer':
    case 'float':
    case 'percentage':
    case 'duration':
    case 'timestamp':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array':
      return 'string[]';
    default:
      return 'string';
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function generateRust(input: CodegenInput): string {
  const fn = identifier(input.name).replace(/([A-Z])/g, '_$1').toLowerCase();
  return `// Generated by Pixansi from "${input.name}" (${input.width}x${input.height}).
use std::collections::HashMap;

const FRAME: &str = "${escapeForRust(input.ansi)}";

/// Renders the frame, replacing {{name}} placeholders with the supplied values.
pub fn ${fn}(values: &HashMap<&str, String>) -> String {
    let mut out = String::with_capacity(FRAME.len());
    let mut rest = FRAME;
    while let Some(start) = rest.find("{{") {
        out.push_str(&rest[..start]);
        let after = &rest[start + 2..];
        match after.find("}}") {
            Some(end) => {
                let raw = after[..end].trim();
                let name = raw.split('|').next().unwrap_or(raw).trim();
                match values.get(name) {
                    Some(value) => out.push_str(value),
                    None => {
                        out.push_str("{{");
                        out.push_str(&after[..end]);
                        out.push_str("}}");
                    }
                }
                rest = &after[end + 2..];
            }
            None => {
                out.push_str(&rest[start..]);
                rest = "";
            }
        }
    }
    out.push_str(rest);
    out
}

// Example:
// let mut values = HashMap::new();
${input.variables
  .slice(0, 2)
  .map((v) => `// values.insert("${v.name}", "${String(v.previewValue ?? '')}".to_string());`)
  .join('\n')}
// print!("{}", ${fn}(&values));
`;
}

function generatePython(input: CodegenInput): string {
  const fn = identifier(input.name).replace(/([A-Z])/g, '_$1').toLowerCase();
  return `# Generated by Pixansi from "${input.name}" (${input.width}x${input.height}).
import re
import sys

FRAME = "${escapeForPython(input.ansi)}"

DEFAULTS = {
${input.variables.map((v) => `    ${JSON.stringify(v.name)}: ${JSON.stringify(String(v.defaultValue ?? ''))},`).join('\n')}
}

_PLACEHOLDER = re.compile(r"\\{\\{\\s*([A-Za-z_][A-Za-z0-9_]*)\\s*(?:\\|[^}]*)?\\}\\}")


def ${fn}(**values):
    """Render the frame with {{name}} placeholders substituted."""
    merged = {**DEFAULTS, **values}
    return _PLACEHOLDER.sub(lambda m: str(merged.get(m.group(1), m.group(0))), FRAME)


if __name__ == "__main__":
    sys.stdout.write(${fn}())
`;
}

function generateGo(input: CodegenInput): string {
  const fn = capitalize(identifier(input.name));
  return `// Generated by Pixansi from "${input.name}" (${input.width}x${input.height}).
package screens

import (
	"regexp"
	"strings"
)

const frame = "${escapeForRust(input.ansi)}"

var placeholder = regexp.MustCompile(\`\\{\\{\\s*([A-Za-z_][A-Za-z0-9_]*)\\s*(?:\\|[^}]*)?\\}\\}\`)

var defaults = map[string]string{
${input.variables.map((v) => `\t${JSON.stringify(v.name)}: ${JSON.stringify(String(v.defaultValue ?? ''))},`).join('\n')}
}

// ${fn} renders the frame with the supplied values substituted.
func ${fn}(values map[string]string) string {
	return placeholder.ReplaceAllStringFunc(frame, func(match string) string {
		name := placeholder.FindStringSubmatch(match)[1]
		if v, ok := values[name]; ok {
			return v
		}
		if v, ok := defaults[name]; ok {
			return v
		}
		return match
	})
}

var _ = strings.TrimSpace
`;
}

/** "Copy as" helpers for a selected region. */
export function copyAs(
  target: 'text' | 'ansi' | 'json' | 'typescript' | 'rust' | 'python',
  payload: { text: string; json: string },
): string {
  switch (target) {
    case 'text':
    case 'ansi':
      return payload.text;
    case 'json':
      return payload.json;
    case 'typescript':
      return `const art = \`${escapeForTs(payload.text)}\`;`;
    case 'rust':
      return `let art = "${escapeForRust(payload.text)}";`;
    case 'python':
      return `art = "${escapeForPython(payload.text)}"`;
  }
}
